type ExpiryListener = (type: 'soon' | 'expired') => void;

class TokenExpiryManager {
  private accessExpMs: number | null = null;
  private refreshExpMs: number | null = null;
  private soonTimer: number | null = null;
  private expiredTimer: number | null = null;
  private expireSoonListeners: Set<ExpiryListener> = new Set();
  private expiredListeners: Set<ExpiryListener> = new Set();
  private warnMsBefore: number = 2 * 60 * 1000; // 2 minutes

  setWarnLeadTime(ms: number) {
    this.warnMsBefore = ms;
    this.reschedule();
  }

  onExpireSoon(listener: ExpiryListener)
  {
    this.expireSoonListeners.add(listener);
    return () => this.expireSoonListeners.delete(listener);
  }

  onExpired(listener: ExpiryListener) 
  {
    this.expiredListeners.add(listener);
    return () => this.expiredListeners.delete(listener);
  }

  setTokens(accessToken?: string | null, refreshToken?: string | null) 
  {
    this.accessExpMs = this.decodeExpMs(accessToken) ?? null;
    this.refreshExpMs = this.decodeExpMs(refreshToken) ?? null;
    if (this.accessExpMs) {
      try {
        localStorage.setItem('access-exp', String(this.accessExpMs));
      } catch {}
    }
    if (this.refreshExpMs) {
      try {
        localStorage.setItem('refresh-exp', String(this.refreshExpMs));
      } catch {}
    }
    this.reschedule();
  }

  clear() {
    this.accessExpMs = null;
    this.refreshExpMs = null;
    this.clearTimers();
    try {
      localStorage.removeItem('access-exp');
      localStorage.removeItem('refresh-exp');
    } catch {}
  }

  initFromStorage() {
    try {
      const a = localStorage.getItem('access-exp');
      const r = localStorage.getItem('refresh-exp');
      this.accessExpMs = a ? Number(a) : null;
      this.refreshExpMs = r ? Number(r) : null;
      this.reschedule();
    } catch {}
  }

  handleVisibilityOrFocusCheck = () => {
    const now = Date.now();
    if (this.accessExpMs && now >= this.accessExpMs) {
      this.emitExpired();
    } else if (this.accessExpMs && now >= (this.accessExpMs - this.warnMsBefore)) {
      this.emitExpireSoon();
    }
  };

  private reschedule() {
    this.clearTimers();
    const now = Date.now();
    if (!this.accessExpMs) return;
    const soonDelay = Math.max(0, this.accessExpMs - this.warnMsBefore - now);
    const expDelay = Math.max(0, this.accessExpMs - now);
    this.soonTimer = window.setTimeout(() => this.emitExpireSoon(), soonDelay);
    this.expiredTimer = window.setTimeout(() => this.emitExpired(), expDelay);
  }

  private clearTimers() {
    if (this.soonTimer !== null) {
      clearTimeout(this.soonTimer);
      this.soonTimer = null;
    }
    if (this.expiredTimer !== null) {
      clearTimeout(this.expiredTimer);
      this.expiredTimer = null;
    }
  }

  private emitExpireSoon() {
    this.expireSoonListeners.forEach((l) => l('soon'));
  }

  private emitExpired() {
    this.expiredListeners.forEach((l) => l('expired'));
  }

  private decodeExpMs(token?: string | null): number | undefined {
    if (!token) return undefined;
    try {
      const payload = JSON.parse(atob(token.split('.')[1] || ''));
      if (payload && typeof payload.exp === 'number') {
        return payload.exp * 1000;
      }
    } catch (error) { // 에러 무시 
      console.error("JWT 디코딩 실패:", error);
      return undefined;
    }
  }
}

export const tokenExpiryManager = new TokenExpiryManager();


